import { Check, CircleCheck } from 'lucide-react';
import { Button } from '@/components/ui';
import { TIERS } from '@/constants/billing';
import type { SubscriptionTier } from '@/types';

export type PlanCardState =
  /** This is the household's current tier. No CTA. Primary border + check icon. */
  | 'active'
  /**
   * The household's current tier with a scheduled downgrade pending.
   * Same treatment as `active` plus the scheduled-change footer + the
   * "Cancel scheduled change" link.
   */
  | 'active-scheduled'
  /** A tier the household can switch to. Standard border + CTA. */
  | 'inactive'
  /**
   * The target of a scheduled downgrade. Standard border, no CTA,
   * no scheduled copy — purely visual cue that this is where the
   * household is heading.
   */
  | 'scheduled-target';

export interface PlanCardProps {
  tier: SubscriptionTier;
  state: PlanCardState;
  /** CTA label shown when state = 'inactive'. */
  ctaLabel?: string;
  onCtaClick?: () => void;
  ctaLoading?: boolean;
  ctaDisabled?: boolean;
  /** ISO timestamp for the scheduled change (state = 'active-scheduled'). */
  scheduledChangeAt?: string;
  /** The tier the household will switch to (state = 'active-scheduled'). */
  scheduledTargetTier?: SubscriptionTier;
  onCancelScheduled?: () => void;
}

function formatScheduledDate(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export function PlanCard({
  tier,
  state,
  ctaLabel,
  onCtaClick,
  ctaLoading,
  ctaDisabled,
  scheduledChangeAt,
  scheduledTargetTier,
  onCancelScheduled,
}: PlanCardProps) {
  const config = TIERS[tier];
  const isActiveLike = state === 'active' || state === 'active-scheduled';

  // The "this is your plan" cue is `success` (#17825a), not the brand
  // `primary` (#264133) — both are greens, which is exactly how the card
  // shipped wearing the wrong one. Figma's Plan frame (8437:29841) draws
  // the selected card's border and its check glyph in the success green,
  // the same token the "Invite sent" tick uses (YAR-230).
  return (
    <div
      className={`flex flex-col gap-4 rounded-xl border bg-background px-4 py-3 ${
        isActiveLike ? 'border-success' : 'border-border'
      }`}
      data-state={state}
      data-tier={tier}
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <p className="text-xl font-medium text-text-heading">
            {config.label}
          </p>
          <div className="flex items-center gap-2">
            {state !== 'active' && (
              <span className="text-xl font-medium text-text-heading">
                {tier === 'FREE' ? '' : config.priceLabel}
              </span>
            )}
            {state === 'active' && (
              <span className="text-xl font-medium text-text-heading">
                {tier === 'FREE' ? '' : config.priceLabel}
              </span>
            )}
            {isActiveLike && (
              <CircleCheck
                size={28}
                className="text-success shrink-0"
                aria-label="Current plan"
              />
            )}
          </div>
        </div>
        <p className="text-sm text-muted-foreground">{config.tagline}</p>
        <ul className="flex flex-col gap-2">
          {config.features.map((feature) => (
            <li
              key={feature}
              className="flex items-center gap-2 text-sm text-muted-foreground"
            >
              <Check
                size={20}
                className="text-foreground shrink-0"
              />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      {state === 'inactive' && ctaLabel && (
        <Button
          variant="primary"
          fullWidth
          onClick={onCtaClick}
          loading={ctaLoading}
          disabled={ctaDisabled}
        >
          {ctaLabel}
        </Button>
      )}

      {state === 'active-scheduled' && scheduledTargetTier && (
        <div className="flex flex-col gap-1 border-t border-border pt-3">
          <p className="text-sm text-muted-foreground">
            Active until {formatScheduledDate(scheduledChangeAt)}, then you'll
            switch to {TIERS[scheduledTargetTier].label}.
          </p>
          <button
            type="button"
            onClick={onCancelScheduled}
            className="self-start text-sm font-medium text-foreground underline hover:text-primary cursor-pointer"
          >
            Cancel scheduled change
          </button>
        </div>
      )}
    </div>
  );
}
